import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Download, ShieldAlert } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { CleanupExecute, CleanupDone } from '@/components/AdminCleanupExecute';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ACTIONS = {
  delete_all: ['Cancellare tutto', 'bg-red-50 text-red-700'],
  delete_except_keep: ['Cancellare, tranne account tenuti', 'bg-orange-50 text-orange-700'],
  reset: ['Azzerare', 'bg-amber-50 text-amber-700'],
  keep: ['Tenere', 'bg-emerald-50 text-emerald-700'],
  unclassified: ['Da decidere', 'bg-secondary text-secondary-foreground'],
};
const fmt = (n) => (n ?? 0).toLocaleString('it-IT');

const Stat = ({ label, value, testid }) => (
  <Card className="border border-border rounded-xl"><CardContent className="p-4">
    <p className="text-xl font-bold font-sans" data-testid={testid}>{value}</p>
    <p className="text-xs text-muted-foreground font-sans mt-1">{label}</p>
  </CardContent></Card>
);

const KeepList = ({ users }) => (
  <Card className="border border-border rounded-xl" data-testid="cleanup-keep-users"><CardContent className="p-4 space-y-2">
    <p className="text-sm font-semibold font-sans">Account che restano ({users.length})</p>
    {users.map(u => (
      <div key={u.user_id} className="flex flex-wrap gap-x-3 text-xs font-sans" data-testid={`cleanup-keep-${u.user_id}`}>
        <span className="font-semibold">{u.email}</span><span className="text-muted-foreground">{u.reason}</span>
        <span className="font-mono text-muted-foreground">{u.user_id}</span>
      </div>
    ))}
    <p className="text-xs text-muted-foreground font-sans pt-1">Per questi account restano anche i codici di sicurezza (TOTP e codici di recupero) e i permessi admin attivi.</p>
  </CardContent></Card>
);

export const AdminCleanupPlanTab = () => {
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState(false);
  const load = () => axios.get(`${API}/admin/cleanup-plan`, { withCredentials: true }).then(r => setPlan(r.data)).catch(() => setError(true));
  useEffect(() => { load(); }, []);
  if (error) return <p className="text-sm font-sans p-6" data-testid="cleanup-plan-error">Impossibile caricare il piano.</p>;
  if (!plan) return <div className="p-10 flex justify-center"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  const s = plan.summary;
  const users = plan.rows.find(r => r.collection === 'users');
  const a2s = s.transactions_by_type.ACTION_2_SMILE || { total: 0, count: 0 };
  return (
    <div className="space-y-4" data-testid="cleanup-plan">
      <div className="flex flex-wrap items-start justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50/60 p-4">
        <div className="flex gap-2 max-w-2xl">
          <ShieldAlert className="w-5 h-5 text-amber-700 shrink-0" strokeWidth={1.5} />
          <p className="text-sm font-sans text-amber-900">Il piano qui sotto mostra cosa verrebbe eliminato e cosa resterebbe nei dati attuali. Nulla viene cancellato finché non completi i 3 passaggi in fondo alla pagina: backup, frase di conferma e codice dell'app.</p>
        </div>
        <Button variant="outline" className="rounded-full" onClick={() => window.open(`${API}/admin/cleanup-plan/users.csv`, '_blank')} data-testid="cleanup-plan-csv-btn">
          <Download className="w-4 h-4 mr-2" strokeWidth={1.5} /> Elenco utenti (CSV)</Button>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label={`Account da cancellare su ${fmt(users.total)}`} value={fmt(users.delete)} testid="cleanup-stat-users" />
        <Stat label="Saldo coin nei portafogli da azzerare" value={fmt(s.wallet_balance_total)} testid="cleanup-stat-balance" />
        <Stat label={`Quote Action2Smile registrate (${fmt(a2s.count)} movimenti)`} value={`€ ${fmt(a2s.total)}`} testid="cleanup-stat-a2s" />
        <Stat label="Foto delle attività collegate" value={fmt(s.gallery_images)} testid="cleanup-stat-images" />
      </div>
      <KeepList users={plan.keep_users} />
      {s.unclassified.length > 0 && <p className="text-xs font-sans text-red-700" data-testid="cleanup-unclassified">Dati non previsti da decidere: {s.unclassified.join(', ')}</p>}
      <Card className="border border-border rounded-xl"><CardContent className="p-0 overflow-x-auto">
        <Table>
          <TableHeader><TableRow>{['Dati', 'Azione', 'Totale', 'Da cancellare', 'Restano', 'Note'].map(h => <TableHead key={h} className="font-sans text-xs">{h}</TableHead>)}</TableRow></TableHeader>
          <TableBody>
            {plan.rows.map(r => (
              <TableRow key={r.collection} data-testid={`cleanup-row-${r.collection}`}>
                <TableCell className="text-xs font-sans"><span className="font-semibold">{r.label}</span><span className="block font-mono text-muted-foreground">{r.collection}</span></TableCell>
                <TableCell><Badge className={`text-[10px] ${ACTIONS[r.action][1]}`}>{ACTIONS[r.action][0]}</Badge></TableCell>
                <TableCell className="text-xs font-sans">{fmt(r.total)}</TableCell>
                <TableCell className="text-xs font-sans font-semibold">{fmt(r.delete)}</TableCell>
                <TableCell className="text-xs font-sans">{fmt(r.keep)}</TableCell>
                <TableCell className="text-xs font-sans text-muted-foreground max-w-xs">{r.note}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>
      {plan.executed ? <CleanupDone run={plan.executed} /> : <CleanupExecute phrase={plan.confirm_phrase} blocked={s.unclassified.length > 0} onDone={load} />}
    </div>
  );
};
